import React from "react";
import useMediaQuery from "@mui/system/useMediaQuery";
import { Typography, useTheme } from "@mui/joy";
import { Box } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import { BasicModalContent, LoadingScreen } from "@dreebit-registry/vsm-microservice-components";
import { resolveErrorMessage } from "../../utils/resolveErrorMessage";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import _ from "lodash";
import { ServiceReminderTdas } from "../../graphql/queries/serviceReminderTdas";
import { CreateServiceReminder } from "../../graphql/mutations/createServiceReminder";
import InstalledBaseTDAForm from "../InstalledBaseTDAForm";
import { isDueDateAfter } from "../../utils/serviceReminderDateValidation";

interface Props {
    open: boolean;
    installedBaseNodeId: string;
    availableServiceTasks: { id: string; name: string }[];
    onClose: () => void;
}

const CreateServiceReminderModal = ({ open, installedBaseNodeId, availableServiceTasks, onClose }: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const { t } = useTranslation();

    const [createServiceReminder] = useMutation(CreateServiceReminder);

    const { data, loading } = useQuery(ServiceReminderTdas, {
        variables: { installedBaseNodeId, typeDefinitionIndex: "SERVICE_REMINDER_CREATE" },
        skip: !installedBaseNodeId || !open,
        fetchPolicy: "network-only",
    });

    // Replace the `defaultServiceTaskId` dynamic loader with a pre-filtered static
    // option list, so already-used service types aren't offered. SearchableMultiSelect
    // uses static `optionValues` only when there is no `dataLoaderConfig`.
    const attributes = _.get(
        data,
        "serviceReminderList.typeDefinitionAttributeList.typeDefinitionAttributes",
        []
    ).map((attr: any) =>
        attr.index === "defaultServiceTaskId"
            ? {
                  ...attr,
                  dataLoaderConfig: null,
                  optionValues: availableServiceTasks.map((task) => ({ value: task.id, name: task.name })),
              }
            : attr
    );

    const handleSubmit = (input: any) => {
        if (!isDueDateAfter(input.nextDueDate, input.lastServiceDate)) {
            toast.error(t("The next due date must be after the last service date."));
            return;
        }

        return createServiceReminder({
            variables: { installedBaseNodeId, input },
            refetchQueries: ["ServiceReminderList"],
        })
            .then((res) => {
                if (res.data?.createServiceReminder?.id) {
                    toast.success(t("Service reminder created successfully."));
                    onClose();
                } else {
                    toast.error(t("Error creating service reminder."));
                }
            })
            .catch((err) => {
                toast.error(resolveErrorMessage(err, t));
            });
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen={isFullScreen}>
            <BasicModalContent
                onClose={onClose}
                title={<Typography level={"titleSmallLight"}>{t("Create service reminder")}</Typography>}
            >
                {loading ? (
                    <Box sx={{ py: "24px" }}>
                        <LoadingScreen size={"sm"} />
                    </Box>
                ) : (
                    <InstalledBaseTDAForm
                        formStyle={{ paddingTop: "0px" }}
                        onSubmit={handleSubmit}
                        onCancel={onClose}
                        submitButtonText={t("Create")}
                        attributes={attributes}
                    />
                )}
            </BasicModalContent>
        </Dialog>
    );
};

export default CreateServiceReminderModal;
